/**
 * Database Adapter - Handles communication with the database proxy API
 * This adapter abstracts the database operations and communicates with the backend
 * Uses the insertWorkflowDatabaseRecord API which handles all operations (insert, update, delete, get)
 *
 * Works in both browser (frontend) and Deno (backend) environments
 *
 * SECURITY NOTE for Deno backend:
 * In concurrent async environments, credentials are stored in a request-scoped context Map
 * (keyed by unique request ID) to prevent credential leakage between requests.
 *
 * For maximum security in async operations, functions can:
 * 1. Pass context directly: await dbAdapter.callApi(op, table, data, where, records, { apiKey, authToken })
 * 2. Or set instance credentials: dbAdapter.setCredentials(apiKey, authToken)
 */

// Environment detection
const isBrowser = typeof window !== "undefined";
const isDeno = typeof Deno !== "undefined";

class DatabaseAdapter {
    constructor() {
        // Get API URL based on environment
        if (isBrowser) {
            const hostname = window?.location?.hostname ?? "";
            const isStaging =
                hostname.includes("staging-apps.agentui.ai") ||
                hostname.includes("staging-api.agentui.ai") ||
                hostname === "staging.agentui.ai";
            this.apiUrl =
                hostname === "localhost" || hostname?.includes("agentui.test")
                    ? "http://localhost:3333"
                    : isStaging
                    ? "https://staging-api.agentui.ai"
                    : "https://api.agentui.ai";
            this.workflowId = window.config?.workflowId;
            // In browser, apiKey is read dynamically via getApiKey()
        } else if (isDeno) {
            this.apiUrl =
                Deno.env.get("API_BASE_URL") || "https://api.agentui.ai";
            this.workflowId = Deno.env.get("WORKFLOW_ID");
            // In Deno, apiKey and token are read dynamically from request-scoped globals
        } else {
            // Node.js fallback
            this.apiUrl = process.env.API_BASE_URL || "https://api.agentui.ai";
            this.workflowId = process.env.WORKFLOW_ID;
            this.apiKey = process.env.AGENTUI_API_KEY;
        }

        // Rate limiting configuration
        // Track calls per entity (collection/table name)
        // Structure: Map<entityName, { count: number, windowStart: number, alertShown: boolean }>
        this._callTracker = new Map();

        // Track which entities have shown alerts in current windows
        // Format: "entityName:windowStart" to ensure alerts reset per window
        this._alertShownSet = new Set();

        // Rate limit configuration: max calls per time window (in milliseconds)
        // Default: 100 calls per 60 seconds (1 minute)
        this._rateLimitConfig = {
            maxCalls: 200,
            windowMs: 60000, // 1 minute
        };

        // Call deduplication: prevent duplicate calls within a short time window
        // Structure: Map<callSignature, { promise: Promise, timestamp: number }>
        this._callDeduplicationMap = new Map();
        this._debounceTimeoutMs = 50; // 50ms debounce window

        // Result cache: cache successful results for faster subsequent calls
        // Structure: Map<callSignature, { result: any, timestamp: number }>
        this._resultCacheMap = new Map();
        this._cacheTimeoutMs = 300; // 300ms cache window

        // Threshold to warn about large result sets (AI hint)
        this._largeResultSetThreshold = 50;

        // Retry configuration for transient failures
        this._retryConfig = {
            maxRetries: 3,
            baseDelayMs: 300,
            maxDelayMs: 5000,
        };

        // Fetch timeout configuration
        this._fetchTimeoutMs = 30000; // 30 seconds

        // Rapid-call detection for debounce suggestions
        // Structure: Map<entityName, Array<{ timestamp, filterString }>>
        this._rapidCallEntries = new Map();
        this._rapidCallSuggestedSet = new Set();
    }

    /**
     * Get request context from the secure context map (Deno only)
     * This prevents credential leakage between concurrent requests
     */
    _getRequestContext() {
        if (!isDeno) return null;

        // Retrieve requestId from AsyncLocalStorage (set in server.ts)
        const storage = globalThis.__AGENTUI_REQUEST_ID_STORAGE__;
        const requestId = storage ? storage.getStore() : null;
        const contexts = globalThis.__AGENTUI_REQUEST_CONTEXTS__;

        if (!requestId || !contexts) return null;

        return contexts.get(requestId) || null;
    }

    /**
     * Get the API key - checks request-scoped context first (for Deno), then config/env
     * Uses secure request context map to prevent credential leakage between requests
     */
    getApiKey() {
        if (isBrowser) {
            return window.config?.apiKey || null;
        } else if (isDeno) {
            // In Deno, get API key from secure request-scoped context
            const context = this._getRequestContext();
            if (context?.apiKey) {
                return context.apiKey;
            }
            // Fall back to env var if no request context
            return Deno.env.get("AGENTUI_API_KEY") || null;
        }
        return this.apiKey || null;
    }

    getServerKey() {
        if (isBrowser) {
            // always null
            return null;
        } else if (isDeno) {
            return Deno.env.get("AGENTUI_SERVER_KEY") || null;
        }
        return process.env.AGENTUI_SERVER_KEY || null;
    }

    /**
     * Get the Bearer token from localStorage (browser) or request-scoped context (Deno)
     * Uses secure request context map to prevent credential leakage between requests
     */
    getToken() {
        const workflowId = this.workflowId;
        if (!workflowId) {
            return null;
        }

        if (isBrowser) {
            const tokenKey = `workflow-auth-${workflowId}`;
            return localStorage.getItem(tokenKey);
        } else if (isDeno) {
            // In Deno, get auth token from secure request-scoped context
            const context = this._getRequestContext();
            return context?.authToken || null;
        }

        // Node.js fallback - no token
        return null;
    }

    /**
     * Set explicit credentials for this adapter instance
     * Use this when you have the credentials from the request context
     * to avoid any race conditions with concurrent requests
     */
    setCredentials(apiKey, authToken) {
        this._explicitApiKey = apiKey;
        this._explicitAuthToken = authToken;
    }

    /**
     * Clear explicit credentials
     */
    clearCredentials() {
        this._explicitApiKey = undefined;
        this._explicitAuthToken = undefined;
    }

    /**
     * Configure rate limiting
     * @param {number} maxCalls - Maximum number of calls allowed per time window
     * @param {number} windowMs - Time window in milliseconds
     */
    setRateLimit(maxCalls, windowMs) {
        this._rateLimitConfig = {
            maxCalls,
            windowMs,
        };
    }

    /**
     * Clean up expired alert entries from the alert set
     * Removes entries for windows that have expired
     */
    _cleanupExpiredAlerts() {
        const now = Date.now();
        const expiredKeys = [];

        // Check all alert entries and find expired ones
        this._alertShownSet.forEach((alertKey) => {
            // Parse entityName and windowStart from key format: "entityName:windowStart"
            // Use lastIndexOf to handle entity names that might contain colons
            const lastColonIndex = alertKey.lastIndexOf(":");
            if (lastColonIndex > 0 && lastColonIndex < alertKey.length - 1) {
                const windowStart = parseInt(
                    alertKey.substring(lastColonIndex + 1),
                    10
                );
                if (
                    !isNaN(windowStart) &&
                    now - windowStart >= this._rateLimitConfig.windowMs
                ) {
                    expiredKeys.push(alertKey);
                }
            }
        });

        // Remove expired entries
        expiredKeys.forEach((key) => this._alertShownSet.delete(key));
    }

    /**
     * Check and track API calls for rate limiting
     * @param {string} entityName - Name of the entity/collection
     * @returns {boolean} - true if within rate limit, false if exceeded
     */
    _checkRateLimit(entityName) {
        const now = Date.now();
        const tracker = this._callTracker.get(entityName);

        // If no tracker exists or window has expired, create/reset tracker
        if (
            !tracker ||
            now - tracker.windowStart >= this._rateLimitConfig.windowMs
        ) {
            // Check if even the first call would exceed the limit (edge case)
            if (this._rateLimitConfig.maxCalls <= 0) {
                return false;
            }

            // Clean up expired alert entries when we detect a window has expired
            // This ensures old alert entries don't accumulate
            this._cleanupExpiredAlerts();

            // Remove old alert entry for this entity if window expired
            if (tracker) {
                const oldKey = `${entityName}:${tracker.windowStart}`;
                this._alertShownSet.delete(oldKey);
            }

            // Create new tracker with new window
            const newWindowStart = now;
            this._callTracker.set(entityName, {
                count: 1,
                windowStart: newWindowStart,
                alertShown: false,
            });

            return true;
        }

        // Ensure alertShown property exists (for backward compatibility)
        if (tracker.alertShown === undefined) {
            tracker.alertShown = false;
        }

        // Increment call count
        tracker.count++;

        // Check if rate limit exceeded
        if (tracker.count > this._rateLimitConfig.maxCalls) {
            console.log(
                `Rate limit exceeded for ${entityName}: ${tracker.count}`
            );
            return false;
        }

        return true;
    }

    /**
     * Get current call count for an entity
     * @param {string} entityName - Name of the entity/collection
     * @returns {number} - Current call count in the current time window
     */
    getCallCount(entityName) {
        const tracker = this._callTracker.get(entityName);
        if (!tracker) return 0;

        const now = Date.now();
        // If window has expired, return 0
        if (now - tracker.windowStart >= this._rateLimitConfig.windowMs) {
            return 0;
        }

        return tracker.count;
    }

    /**
     * Get call statistics for all entities
     * @returns {object} - Object mapping entity names to their call counts
     */
    getCallStatistics() {
        const stats = {};
        const now = Date.now();

        this._callTracker.forEach((tracker, entityName) => {
            // Only include active windows
            if (now - tracker.windowStart < this._rateLimitConfig.windowMs) {
                stats[entityName] = {
                    count: tracker.count,
                    limit: this._rateLimitConfig.maxCalls,
                    windowStart: new Date(tracker.windowStart).toISOString(),
                    windowMs: this._rateLimitConfig.windowMs,
                };
            }
        });

        return stats;
    }

    /**
     * Reset call tracker for a specific entity or all entities
     * @param {string|null} entityName - Entity name to reset, or null to reset all
     */
    resetCallTracker(entityName = null) {
        if (entityName) {
            // Remove all alert entries for this entity
            const tracker = this._callTracker.get(entityName);
            if (tracker) {
                const alertKey = `${entityName}:${tracker.windowStart}`;
                this._alertShownSet.delete(alertKey);
            }
            this._callTracker.delete(entityName);
        } else {
            // Clear all trackers and alert entries
            this._callTracker.clear();
            this._alertShownSet.clear();
        }
    }

    /**
     * Clear the result cache
     * Useful when you want to force fresh data retrieval
     */
    clearCache() {
        this._resultCacheMap.clear();
        this._callDeduplicationMap.clear();
    }

    /**
     * Clear cache entries for a specific table/collection
     * Used when write operations occur to invalidate cached reads for that table
     * @param {string} tableName - Name of the table/collection to clear cache for
     */
    _clearCacheForTable(tableName) {
        if (!tableName) return;

        const keysToDelete = [];

        // Check cache map for entries matching this table
        this._resultCacheMap.forEach((entry, signature) => {
            try {
                const payload = JSON.parse(signature);
                if (payload.tableName === tableName) {
                    keysToDelete.push(signature);
                }
            } catch (e) {
                // If signature can't be parsed, skip it
            }
        });

        // Check deduplication map for entries matching this table
        this._callDeduplicationMap.forEach((entry, signature) => {
            try {
                const payload = JSON.parse(signature);
                if (payload.tableName === tableName) {
                    keysToDelete.push(signature);
                }
            } catch (e) {
                // If signature can't be parsed, skip it
            }
        });

        // Delete matching entries
        keysToDelete.forEach((key) => {
            this._resultCacheMap.delete(key);
            this._callDeduplicationMap.delete(key);
        });
    }

    /**
     * Generate a unique signature for a call based on its parameters
     * This excludes credentials (apiKey, authToken) as they don't affect call identity
     * @param {string} operation - The operation type
     * @param {string} tableName - Name of the table/collection
     * @param {object} data - Data for the operation
     * @param {object} where - Where conditions
     * @param {array} records - Array of records for bulk operations
     * @param {object} options - Additional options (excluding apiKey/authToken)
     * @returns {string} - Unique call signature
     */
    /**
     * Generate a unique signature for a call based on its parameters.
     * Sorts keys to ensure {a:1, b:2} and {b:2, a:1} produce the same signature.
     * Includes user identifier to ensure cache is scoped to the user session (for Deno)
     */
    _generateCallSignature(
        operation,
        tableName,
        data,
        where,
        records,
        options
    ) {
        // Deep sort keys for stable stringification
        const stabilize = (obj) => {
            if (obj === null || typeof obj !== "object" || Array.isArray(obj)) {
                return obj;
            }
            return Object.keys(obj)
                .sort()
                .reduce((acc, key) => {
                    acc[key] = stabilize(obj[key]);
                    return acc;
                }, {});
        };

        const cleanOptions = options ? { ...options } : null;
        if (cleanOptions) {
            delete cleanOptions.apiKey;
            delete cleanOptions.authToken;
        }

        // Get request context to scope cache to user session (Deno only)
        const context = this._getRequestContext();
        const userIdentifier = context?.authToken || "anonymous"; // Ensure cache is scoped to the user session

        const payload = {
            userIdentifier,
            operation,
            tableName,
            data: data ? stabilize(data) : null,
            where: where ? stabilize(where) : null,
            records: records ? records.map((r) => stabilize(r)) : null,
            options: cleanOptions ? stabilize(cleanOptions) : null,
        };

        return JSON.stringify(payload);
    }

    /**
     * Clean up expired call entries from the deduplication map and cache
     * Removes entries older than their respective timeouts
     */
    _cleanupExpiredCalls() {
        const now = Date.now();
        const expiredDebounceKeys = [];
        const expiredCacheKeys = [];

        // Clean up expired debounce entries
        this._callDeduplicationMap.forEach((entry, signature) => {
            if (now - entry.timestamp >= this._debounceTimeoutMs) {
                expiredDebounceKeys.push(signature);
            }
        });

        // Clean up expired cache entries
        this._resultCacheMap.forEach((entry, signature) => {
            if (now - entry.timestamp >= this._cacheTimeoutMs) {
                expiredCacheKeys.push(signature);
            }
        });

        expiredDebounceKeys.forEach((key) =>
            this._callDeduplicationMap.delete(key)
        );
        expiredCacheKeys.forEach((key) => this._resultCacheMap.delete(key));
    }

    /**
     * Handle rate limit exceeded - log error and show alert in browser
     * @param {string} entityName - Name of the entity/collection
     */
    _handleRateLimitExceeded(entityName) {
        const tracker = this._callTracker.get(entityName);
        if (!tracker) return; // Safety check

        const message = `Rate limit exceeded for entity "${entityName}": ${
            tracker.count
        } calls in ${this._rateLimitConfig.windowMs / 1000} seconds (limit: ${
            this._rateLimitConfig.maxCalls
        } calls)`;

        // Log error with entity name (always log, even if alert already shown)
        console.error(`[Rate Limit] ${message}`);

        // Send suggestion to the AI via console.human
        if (!tracker.humanSuggested) {
            tracker.humanSuggested = true;
            const suggestion = `The "${entityName}" table hit the rate limit (${
                this._rateLimitConfig.maxCalls
            } calls per ${
                this._rateLimitConfig.windowMs / 1000
            }s). Reduce the frequency of database calls — add caching, debounce search inputs, paginate instead of refetching all records, or batch multiple operations into one.`;
            if (typeof console.human === "function") {
                console.human(suggestion, {
                    type: "rate_limit_exceeded",
                    entity: entityName,
                    callCount: tracker.count,
                    limit: this._rateLimitConfig.maxCalls,
                    suggestion,
                    actionable: true,
                });
            }
        }

        // Report rate limit to backend telemetry once per entity per window
        if (
            typeof window !== "undefined" &&
            window.reportErrorToBackend &&
            !tracker.reported
        ) {
            tracker.reported = true;
            window.reportErrorToBackend(message, "DATABASE_RATE_LIMIT", {
                entityName,
                callCount: tracker.count,
                limit: this._rateLimitConfig.maxCalls,
            });
        }

        if (isBrowser) {
            // Check if query parameter has isInFrame - check both URLSearchParams and direct search
            const urlParams = new URLSearchParams(window.location.search);
            const searchString = window.location.search || "";
            const hasIsInFrameParam =
                urlParams.has("isInFrame") ||
                searchString.includes("isInFrame=true") ||
                searchString.includes("isInFrame=1");

            // Show alert if isInFrame parameter is present
            if (hasIsInFrameParam) {
                const alertKey = `${entityName}:${tracker.windowStart}`;

                // Check if alert already shown for this entity in this window
                if (!this._alertShownSet.has(alertKey)) {
                    // Add to set immediately before showing alert to prevent race conditions
                    this._alertShownSet.add(alertKey);
                    tracker.alertShown = true; // Also update tracker for consistency

                    // Use setTimeout to ensure alert shows even if error is thrown immediately after
                    setTimeout(() => {
                        alert(
                            `Rate Limit Exceeded\n\n${message}\n\nPlease tell the AI to reduce the frequency of database operations for this ${entityName}.`
                        );
                    }, 0);
                }
            }
        }
    }

    /**
     * Determine if an error is retryable (transient)
     * @param {Error} error - The error to check
     * @param {number} httpStatus - HTTP status code if available
     * @returns {boolean}
     */
    _isRetryableError(error, httpStatus) {
        // Network errors (fetch failed, timeout, etc.)
        if (!httpStatus && error.name !== "AbortError") return true;
        // Server errors (5xx) are retryable
        if (httpStatus >= 500 && httpStatus < 600) return true;
        // 429 Too Many Requests (server-side rate limit)
        if (httpStatus === 429) return true;
        // 408 Request Timeout
        if (httpStatus === 408) return true;
        return false;
    }

    /**
     * Execute fetch with a timeout using AbortController
     * @param {string} endpoint - The URL to fetch
     * @param {object} fetchOptions - Options to pass to fetch
     * @returns {Promise<Response>}
     */
    async _fetchWithTimeout(endpoint, fetchOptions) {
        const controller = new AbortController();
        const timeoutId = setTimeout(
            () => controller.abort(),
            this._fetchTimeoutMs
        );
        try {
            const response = await fetch(endpoint, {
                ...fetchOptions,
                signal: controller.signal,
            });
            return response;
        } catch (error) {
            if (error.name === "AbortError") {
                throw new Error(
                    `Request timed out after ${
                        this._fetchTimeoutMs / 1000
                    } seconds`
                );
            }
            throw error;
        } finally {
            clearTimeout(timeoutId);
        }
    }

    /**
     * Extract string values from a filter object for prefix comparison
     * e.g. { name: { $icontains: "rea" } } => "rea"
     * @param {object} where - Filter conditions
     * @returns {string} Concatenated string values from the filter
     */
    _extractFilterStrings(where) {
        if (!where || typeof where !== "object") return "";
        const strings = [];
        const extract = (obj) => {
            for (const val of Object.values(obj)) {
                if (typeof val === "string") strings.push(val);
                else if (val && typeof val === "object") extract(val);
            }
        };
        extract(where);
        return strings.join("|");
    }

    /**
     * Check if filter strings show an incremental prefix pattern
     * e.g. ["r", "re", "rea", "reac", "react"] — each is a prefix of the next
     * @param {Array<string>} filterStrings - Recent filter string values
     * @returns {boolean} True if pattern looks like search-as-you-type
     */
    _isIncrementalPattern(filterStrings) {
        if (filterStrings.length < 3) return false;
        let prefixCount = 0;
        for (let i = 1; i < filterStrings.length; i++) {
            const prev = filterStrings[i - 1];
            const curr = filterStrings[i];
            if (!prev || !curr) continue;
            // Check if current starts with previous (typing) or previous starts with current (deleting)
            if (curr.startsWith(prev) || prev.startsWith(curr)) {
                prefixCount++;
            }
        }
        // At least 60% of transitions should be incremental
        return prefixCount >= (filterStrings.length - 1) * 0.6;
    }

    /**
     * Detect rapid successive read calls that indicate missing debounce/throttle
     * Looks for incremental filter patterns (search-as-you-type) to avoid
     * false positives from legitimate rapid calls (e.g., component mount)
     * @param {string} entityName - Name of the entity/collection
     * @param {number} now - Current timestamp
     * @param {object} where - Filter conditions
     */
    _detectRapidCalls(entityName, now, where) {
        if (this._rapidCallSuggestedSet.has(entityName)) return;

        const filterString = this._extractFilterStrings(where);
        let entries = this._rapidCallEntries.get(entityName);
        if (!entries) {
            entries = [];
            this._rapidCallEntries.set(entityName, entries);
        }

        entries.push({ timestamp: now, filterString });

        // Keep only entries from the last 3 seconds
        const cutoff = now - 3000;
        while (entries.length > 0 && entries[0].timestamp < cutoff) {
            entries.shift();
        }

        // Need at least 5 calls in the window
        if (entries.length < 5) return;

        // Check if filters are changing (not identical repeated calls — those are handled by dedup)
        const filterStrings = entries.map((e) => e.filterString);
        const uniqueFilters = new Set(filterStrings);
        if (uniqueFilters.size < 3) return; // Same filter repeated = not search-as-you-type

        // Check for incremental prefix pattern (strong signal)
        if (this._isIncrementalPattern(filterStrings)) {
            this._rapidCallSuggestedSet.add(entityName);
            const suggestion = `The "${entityName}" table is being queried on every keystroke (${entries.length} calls in under 3 seconds with incrementally changing filters). Add a debounce (300-500ms) to the search input so it only queries after the user stops typing. This will reduce unnecessary API calls and improve performance.`;
            if (typeof console.human === "function") {
                console.human(suggestion, {
                    type: "debounce_suggestion",
                    entity: entityName,
                    callCount: entries.length,
                    suggestion,
                    actionable: true,
                });
            }
        }
    }

    /**
     * Sleep for a given number of milliseconds
     * @param {number} ms
     * @returns {Promise<void>}
     */
    _sleep(ms) {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    /**
     * Calculate delay for exponential backoff with jitter
     * @param {number} attempt - Current retry attempt (0-based)
     * @returns {number} - Delay in milliseconds
     */
    _getRetryDelay(attempt) {
        const delay = Math.min(
            this._retryConfig.baseDelayMs * Math.pow(2, attempt),
            this._retryConfig.maxDelayMs
        );
        // Add jitter: random value between 50% and 100% of calculated delay
        return delay * (0.5 + Math.random() * 0.5);
    }

    /**
     * Add AI-friendly pagination hint to results when the dataset is large.
     * Mutates the result array by attaching a _paginationHint property.
     * @param {Array} result - The array of records returned
     * @param {string} collectionName - Name of the collection
     * @param {object} options - The options that were used for the query
     */
    _addPaginationHintIfNeeded(result, collectionName, options) {
        if (!Array.isArray(result)) return;
        if (result.length < this._largeResultSetThreshold) return;

        const usedLimit = options?.limit;
        const usedOffset = options?.offset || 0;

        result._paginationHint = {
            message:
                `Large result set detected for "${collectionName}": ${result.length} records returned. ` +
                `For better performance, use pagination with limit and offset parameters. ` +
                `Example: db.list("${collectionName}", sort, 50, 0) for the first page, ` +
                `db.list("${collectionName}", sort, 50, 50) for the second page.`,
            recordCount: result.length,
            currentOffset: usedOffset,
            currentLimit: usedLimit || "none (all records)",
            suggestion:
                "Use limit and offset to paginate through records instead of fetching all at once.",
        };

        console.warn(`[DB Performance] ${result._paginationHint.message}`);
    }

    /**
     * Call the database API with specified operation
     * @param {string} operation - The operation type (insert, update, delete, getmany, etc.)
     * @param {string} tableName - Name of the table/collection
     * @param {object} data - Data for the operation
     * @param {object} where - Where conditions (MongoDB-style operators supported)
     * @param {array} records - Array of records for bulk operations
     * @param {object} options - Additional options: { sort, limit, offset, apiKey, authToken }
     */
    async callApi(
        operation,
        tableName,
        data = null,
        where = null,
        records = null,
        options = null
    ) {
        try {
            if (!this.workflowId) {
                throw new Error(
                    "Workflow ID is required. Make sure workflowId is configured."
                );
            }

            // At the start of callApi, if this is a write operation, clear the cache for this table
            const writeOperations = [
                "insert",
                "update",
                "delete",
                "bulkinsert",
                "bulkupdate",
            ];
            if (
                writeOperations.includes(operation.toLowerCase()) &&
                tableName
            ) {
                this._clearCacheForTable(tableName);
            }

            // Generate call signature for deduplication and caching
            const callSignature = this._generateCallSignature(
                operation,
                tableName,
                data,
                where,
                records,
                options
            );
            const now = Date.now();

            // Skip caching in backend (Deno) - only use cache in browser
            if (!isDeno) {
                // Clean up expired entries periodically (moved to top to match User.js behavior)
                this._cleanupExpiredCalls();

                // First, check the cache (300ms window) - return cached result if available
                const cachedResult = this._resultCacheMap.get(callSignature);
                if (
                    cachedResult &&
                    now - cachedResult.timestamp < this._cacheTimeoutMs
                ) {
                    // Return cached result immediately
                    return cachedResult.result;
                }

                // Second, check for duplicate calls within debounce window (50ms)
                // This prevents duplicate API calls when the same call is made rapidly
                const existingCall =
                    this._callDeduplicationMap.get(callSignature);
                if (
                    existingCall &&
                    now - existingCall.timestamp < this._debounceTimeoutMs
                ) {
                    // Return the existing promise if call was made within debounce window
                    return existingCall.promise;
                }
            }

            // Check rate limit for this entity - only check if we're actually making the call
            const entityName = tableName || "unknown";

            // Detect rapid successive read calls (search-as-you-type without debounce)
            if (operation.toLowerCase() === "getmany" && isBrowser) {
                this._detectRapidCalls(entityName, now, where);
            }

            const withinRateLimit = this._checkRateLimit(entityName);

            if (!withinRateLimit) {
                this._handleRateLimitExceeded(entityName);
                throw new Error(
                    `Rate limit exceeded for entity "${entityName}". Maximum ${
                        this._rateLimitConfig.maxCalls
                    } calls allowed per ${
                        this._rateLimitConfig.windowMs / 1000
                    } seconds.`
                );
            }

            // Get credentials - priority: explicit options > instance credentials > auto-detected
            const apiKey =
                options?.apiKey || this._explicitApiKey || this.getApiKey();
            const token =
                options?.authToken ||
                this._explicitAuthToken ||
                this.getToken();

            // Build the endpoint URL — prefer Lambda Function URL (no 1MB limit),
            // fall back to a secondary Lambda URL, then to the API/ALB route.
            const lambdaUrl =
                typeof window !== "undefined"
                    ? window.__ENV__?.DATABASE_RECORDS_LAMBDA_URL
                    : typeof process !== "undefined"
                    ? process.env.DATABASE_RECORDS_LAMBDA_URL
                    : null;
            const lambdaFallbackUrl =
                typeof window !== "undefined"
                    ? window.__ENV__?.DATABASE_RECORDS_FALLBACK_URL
                    : typeof process !== "undefined"
                    ? process.env.DATABASE_RECORDS_FALLBACK_URL
                    : null;
            const buildRecordsUrl = (base) =>
                `${base.replace(/\/$/, "")}/workflows/${
                    this.workflowId
                }/auth/database/records`;
            const ecsFallbackEndpoint = `${this.apiUrl}/workflows/${this.workflowId}/auth/database/records`;
            let endpoint = lambdaUrl
                ? buildRecordsUrl(lambdaUrl)
                : ecsFallbackEndpoint;
            // Ordered chain to try if the primary endpoint fails. Each entry is
            // distinct from the primary so we don't retry the same URL.
            const fallbackChain = [];
            if (
                lambdaFallbackUrl &&
                lambdaFallbackUrl !== lambdaUrl &&
                endpoint !== buildRecordsUrl(lambdaFallbackUrl)
            ) {
                fallbackChain.push(buildRecordsUrl(lambdaFallbackUrl));
            }
            if (endpoint !== ecsFallbackEndpoint) {
                fallbackChain.push(ecsFallbackEndpoint);
            }

            // For delete operations, add important info to query parameters
            // This ensures compatibility with proxies that might strip DELETE bodies
            if (operation === "delete" && (data?.key || where?.key)) {
                const url = new URL(endpoint);
                const key = data?.key || where?.key;
                if (key) url.searchParams.append("key", key);
                if (tableName) url.searchParams.append("tableName", tableName);
                endpoint = url.toString();
            }

            // Build payload matching insertWorkflowDatabaseRecord expectations
            const payload = {
                tableName: tableName,
                operation: operation,
            };

            if (data) {
                payload.data = data;
            }

            if (where) {
                payload.where = where;
            }

            if (records) {
                payload.records = records;
            }

            // Add sort, limit, offset if provided (for backend processing)
            if (options?.sort !== undefined) {
                payload.sort = options.sort;
            }

            if (options?.limit !== undefined) {
                payload.limit = options.limit;
            }

            if (options?.offset !== undefined) {
                payload.offset = options.offset;
            }

            // Build headers
            const headers = {
                "Content-Type": "application/json",
            };

            // Add authentication - Bearer token for browser, API key for backend
            if (token) {
                headers["Authorization"] = `Bearer ${token}`;
            }

            const serverKey = this.getServerKey();
            if (serverKey) {
                headers["x-server-key"] = serverKey;
            }

            if (apiKey) {
                headers["x-api-key"] = apiKey;
            }

            // Create the API call promise with retry logic
            const fetchOptions = {
                method: "POST",
                headers,
                body: JSON.stringify(payload),
            };

            const apiCallPromise = (async () => {
                let lastError = null;
                const maxAttempts = this._retryConfig.maxRetries + 1;

                for (let attempt = 0; attempt < maxAttempts; attempt++) {
                    try {
                        // Use fetch with timeout
                        const response = await this._fetchWithTimeout(
                            endpoint,
                            fetchOptions
                        );

                        if (!response.ok) {
                            const errorText = await response
                                .text()
                                .catch(() => "");
                            let errorData = {};
                            try {
                                errorData = errorText
                                    ? JSON.parse(errorText)
                                    : {};
                            } catch (e) {
                                // Not JSON
                            }
                            const err = new Error(
                                errorData.error ||
                                    `HTTP error ${response.status} on ${operation} ${tableName}`
                            );
                            err._dbMeta = {
                                httpStatus: response.status,
                                responseBody: (
                                    errorData.error ||
                                    errorText ||
                                    ""
                                ).slice(0, 500),
                                endpoint,
                            };

                            // Check if retryable and we have retries left
                            if (
                                this._isRetryableError(err, response.status) &&
                                attempt < maxAttempts - 1
                            ) {
                                const delay = this._getRetryDelay(attempt);
                                console.warn(
                                    `[DB Retry] ${operation} ${tableName} failed (HTTP ${
                                        response.status
                                    }), retrying in ${Math.round(
                                        delay
                                    )}ms (attempt ${attempt + 1}/${
                                        this._retryConfig.maxRetries
                                    })`
                                );
                                await this._sleep(delay);
                                continue;
                            }

                            throw err;
                        }

                        // Handle 204 No Content
                        if (response.status === 204) {
                            return null;
                        }

                        // Safely parse JSON from response text
                        const text = await response.text();
                        const responseData = text ? JSON.parse(text) : {};

                        // Handle response - check for error status first
                        if (
                            responseData?.status &&
                            responseData.status >= 400
                        ) {
                            const err = new Error(
                                responseData.error ||
                                    `Database ${operation} failed on ${tableName} (status ${responseData.status})`
                            );
                            err._dbMeta = {
                                httpStatus: responseData.status,
                                responseBody:
                                    responseData.error ||
                                    JSON.stringify(responseData).slice(0, 500),
                                endpoint,
                            };
                            throw err;
                        }

                        // Handle response - the API may return data directly or wrapped in a response object
                        if (responseData?.data !== undefined) {
                            const recordCount = Array.isArray(responseData.data)
                                ? responseData.data.length
                                : 0;
                            if (recordCount >= 2000) {
                                const hasMore = responseData.hasMore;
                                const suggestion = `The "${tableName}" table returned ${recordCount} records${
                                    hasMore ? " and more exist" : ""
                                }. Add pagination to this view — show 50 records per page with next/previous buttons to improve load times and make the data easier to browse.`;
                                if (typeof console.human === "function") {
                                    console.human(suggestion, {
                                        type: "pagination_suggestion",
                                        tableName,
                                        returnedCount: recordCount,
                                        hasMore,
                                        limit: responseData.limit,
                                        offset: responseData.offset,
                                        suggestion,
                                        actionable: true,
                                    });
                                } else {
                                    console.log(
                                        `[Database] "${tableName}" returned ${recordCount} records. Consider adding pagination.`
                                    );
                                }
                            }
                            return responseData.data;
                        }
                        return responseData;
                    } catch (error) {
                        lastError = error;

                        // Don't retry non-retryable errors or if out of retries
                        const httpStatus = error._dbMeta?.httpStatus;
                        if (
                            !this._isRetryableError(error, httpStatus) ||
                            attempt >= maxAttempts - 1
                        ) {
                            throw error;
                        }

                        const delay = this._getRetryDelay(attempt);
                        console.warn(
                            `[DB Retry] ${operation} ${tableName} failed (${
                                error.message
                            }), retrying in ${Math.round(delay)}ms (attempt ${
                                attempt + 1
                            }/${this._retryConfig.maxRetries})`
                        );
                        await this._sleep(delay);
                    }
                }

                // Should not reach here, but safety net
                throw lastError;
            })();

            // Store the promise in deduplication map (browser only)
            if (!isDeno) {
                this._callDeduplicationMap.set(callSignature, {
                    promise: apiCallPromise,
                    timestamp: now,
                });
            }

            // Wait for the promise and cache the result on success
            try {
                let result;
                try {
                    result = await apiCallPromise;
                } catch (lambdaErr) {
                    // Walk the fallback chain (secondary Lambda URL → ECS API).
                    // First success wins; if all fail, rethrow the last error.
                    if (fallbackChain.length === 0) {
                        throw lambdaErr;
                    }
                    let fallbackSettled = false;
                    let lastFallbackErr = lambdaErr;
                    for (const fbEndpoint of fallbackChain) {
                        try {
                            console.warn(
                                `[DB Fallback] ${operation} ${tableName} failed at ${endpoint}, trying ${fbEndpoint}`,
                                lastFallbackErr.message
                            );
                            const fallbackResponse =
                                await this._fetchWithTimeout(
                                    fbEndpoint,
                                    fetchOptions
                                );
                            if (!fallbackResponse.ok) {
                                const errText = await fallbackResponse
                                    .text()
                                    .catch(() => "");
                                const err = new Error(
                                    `HTTP error ${fallbackResponse.status} on ${operation} ${tableName}`
                                );
                                err._dbMeta = {
                                    httpStatus: fallbackResponse.status,
                                    responseBody: errText.slice(0, 500),
                                    endpoint: fbEndpoint,
                                };
                                throw err;
                            }
                            if (fallbackResponse.status === 204) {
                                result = null;
                            } else {
                                const text = await fallbackResponse.text();
                                const responseData = text
                                    ? JSON.parse(text)
                                    : {};
                                if (
                                    responseData?.status &&
                                    responseData.status >= 400
                                ) {
                                    const err = new Error(
                                        responseData.error ||
                                            `Database ${operation} failed on ${tableName} (status ${responseData.status})`
                                    );
                                    err._dbMeta = {
                                        httpStatus: responseData.status,
                                        responseBody:
                                            responseData.error ||
                                            JSON.stringify(responseData).slice(
                                                0,
                                                500
                                            ),
                                        endpoint: fbEndpoint,
                                    };
                                    throw err;
                                }
                                result =
                                    responseData?.data !== undefined
                                        ? responseData.data
                                        : responseData;
                            }
                            fallbackSettled = true;
                            break;
                        } catch (fbErr) {
                            lastFallbackErr = fbErr;
                        }
                    }
                    if (!fallbackSettled) {
                        throw lastFallbackErr;
                    }
                }

                // Cache the successful result for 300ms (browser only)
                if (!isDeno) {
                    this._resultCacheMap.set(callSignature, {
                        result: result,
                        timestamp: now,
                    });

                    // Remove from debounce map after successful completion
                    // (cache will handle subsequent calls within 300ms)
                    this._callDeduplicationMap.delete(callSignature);
                }

                return result;
            } catch (error) {
                // Remove from both maps on error so retries can work (browser only)
                if (!isDeno) {
                    this._callDeduplicationMap.delete(callSignature);
                    this._resultCacheMap.delete(callSignature);
                }
                throw error;
            }
        } catch (error) {
            console.error(`Database API error (${operation}):`, error);
            // Extract error message
            const errorMessage = error.message || "Unknown error";

            // Report to backend telemetry
            if (typeof window !== "undefined" && window.reportErrorToBackend) {
                const dbMeta = error._dbMeta || {};
                window.reportErrorToBackend(errorMessage, "DATABASE_ERROR", {
                    operation,
                    tableName,
                    apiUrl: dbMeta.endpoint || this.apiUrl,
                    httpStatus:
                        dbMeta.httpStatus ||
                        error.status ||
                        error.response?.status,
                    responseBody: dbMeta.responseBody,
                    stack: error.stack?.slice(0, 1000),
                });
            }

            throw new Error(errorMessage);
        }
    }

    /**
     * List all records from a collection
     * @param {string} collectionName - Name of the collection/table
     * @param {string|object|array} sort - Sort specification:
     *   - String: "fieldName" (ASC) or "-fieldName" (DESC)
     *   - Object: { field: "asc" }, { field: "desc" }, { field: 1 }, { field: -1 }
     *   - Extended: { field: "score", direction: "asc", type: "number" } for typed sort
     *   - Array: ["field1", "-field2"] or [{ field: "score", direction: "desc", type: "number" }]
     * @param {number} limit - Maximum number of records to return
     * @param {number} offset - Number of records to skip (for pagination)
     */
    async list(collectionName, sort = null, limit = null, offset = null) {
        try {
            const options = {};
            if (sort !== null) options.sort = sort;
            if (limit !== null) options.limit = limit;
            if (offset !== null) options.offset = offset;

            const result = await this.callApi(
                "getmany",
                collectionName,
                null,
                null,
                null,
                Object.keys(options).length > 0 ? options : null
            );

            this._addPaginationHintIfNeeded(result, collectionName, options);
            return result;
        } catch (error) {
            console.error(`Error listing ${collectionName}:`, error);
            throw error;
        }
    }

    /**
     * Filter records based on conditions
     * All filtering, sorting, and pagination is done on the backend for better performance.
     *
     * Supports MongoDB-like operators:
     * - Comparison: $eq, $ne, $gt, $gte, $lt, $lte
     * - Array: $in, $nin
     * - String: $contains, $like, $icontains, $ilike, $startsWith, $endsWith
     * - Regex: $regex
     * - Logical: $or, $and, $not
     * - Existence: $exists
     *
     * @param {string} collectionName - Name of the collection/table
     * @param {object} conditions - Filter conditions (MongoDB-style operators supported)
     * @param {string|object|array} sort - Sort specification:
     *   - String: "fieldName" (ASC) or "-fieldName" (DESC)
     *   - Object: { field: "asc" }, { field: "desc" }, { field: 1 }, { field: -1 }
     *   - Extended: { field: "score", direction: "asc", type: "number" } for typed sort
     *   - Array: ["field1", "-field2"] or [{ field: "score", direction: "desc", type: "number" }]
     * @param {number} limit - Maximum number of records to return
     * @param {number} offset - Number of records to skip (for pagination)
     */
    async filter(
        collectionName,
        conditions,
        sort = null,
        limit = null,
        offset = null
    ) {
        try {
            const options = {};
            if (sort !== null) options.sort = sort;
            if (limit !== null) options.limit = limit;
            if (offset !== null) options.offset = offset;

            const result = await this.callApi(
                "getmany",
                collectionName,
                null,
                conditions,
                null,
                Object.keys(options).length > 0 ? options : null
            );

            this._addPaginationHintIfNeeded(result, collectionName, options);
            return result;
        } catch (error) {
            console.error(`Error filtering ${collectionName}:`, error);
            throw error;
        }
    }

    /**
     * Find a record by ID
     */
    async findById(collectionName, id) {
        try {
            const result = await this.callApi(
                "getmany",
                collectionName,
                {},
                { key: id }
            );

            const records = result;

            if (Array.isArray(records) && records.length > 0) {
                return records[0];
            }

            return null;
        } catch (error) {
            console.error(`Error finding ${collectionName} by ID:`, error);
            throw error;
        }
    }

    /**
     * Count records in a collection, optionally filtered by conditions
     * @param {string} collectionName - Name of the collection/table
     * @param {object} conditions - Filter conditions (MongoDB-style operators supported)
     * @returns {Promise<number>} - The count of matching records
     */
    async count(collectionName, conditions = null) {
        try {
            const result = await this.callApi(
                "count",
                collectionName,
                null,
                conditions
            );

            // Backend returns { count: N }
            if (result && typeof result.count === "number") {
                return result.count;
            }

            // Fallback: if result is a number directly
            if (typeof result === "number") {
                return result;
            }

            return 0;
        } catch (error) {
            console.error(`Error counting ${collectionName}:`, error);
            throw error;
        }
    }

    /**
     * Aggregate records with server-side computation.
     * @param {string} collectionName - Name of the collection/table
     * @param {object} options - { metrics, groupBy, filters, having, orderBy, limit, view, refresh }
     * @returns {Promise<object[]>} - Array of aggregate result rows
     */
    async aggregate(collectionName, options = {}) {
        try {
            // View-only read: no metrics needed
            if (options.view && !options.metrics) {
                const result = await this.callApi("aggregate", collectionName, {
                    view: options.view,
                    refresh: options.refresh || false,
                });
                if (result && Array.isArray(result.rows)) {
                    return result.rows;
                }
                if (Array.isArray(result)) {
                    return result;
                }
                return [];
            }

            // Standard or view-with-definition: metrics required
            if (
                !options.metrics ||
                !Array.isArray(options.metrics) ||
                options.metrics.length === 0
            ) {
                throw new Error(
                    'aggregate() requires a "metrics" array with at least one metric. ' +
                        "Each metric needs { field, op, as }. Supported ops: sum, avg, count, min, max. " +
                        'Example: { metrics: [{ field: "amount", op: "sum", as: "total" }] }'
                );
            }

            const validOps = ["sum", "avg", "count", "min", "max"];
            for (const m of options.metrics) {
                if (!m.op || !validOps.includes(m.op)) {
                    throw new Error(
                        `Invalid metric op "${
                            m.op
                        }". Must be one of: ${validOps.join(", ")}. ` +
                            `Usage: { field: "amount", op: "sum", as: "total_amount" }`
                    );
                }
                if (m.op !== "count" && !m.field) {
                    throw new Error(
                        `Metric with op "${m.op}" requires a "field". ` +
                            `Usage: { field: "amount", op: "${m.op}", as: "result" }`
                    );
                }
                if (!m.as) {
                    throw new Error(
                        'Each metric requires an "as" alias for the result. ' +
                            'Usage: { field: "amount", op: "sum", as: "total_amount" }'
                    );
                }
            }

            if (
                options.having &&
                options.having.length > 0 &&
                (!options.groupBy || options.groupBy.length === 0)
            ) {
                throw new Error(
                    '"having" requires "groupBy". HAVING filters aggregate results by group. ' +
                        "Add a groupBy array or remove the having clause."
                );
            }

            const result = await this.callApi(
                "aggregate",
                collectionName,
                options
            );

            if (result && Array.isArray(result.rows)) {
                return result.rows;
            }
            if (Array.isArray(result)) {
                return result;
            }
            return [];
        } catch (error) {
            console.error(`Error aggregating ${collectionName}:`, error);
            throw error;
        }
    }

    /**
     * Create a new record
     */
    async create(collectionName, data) {
        try {
            const result = await this.callApi("insert", collectionName, data);
            if (result === null) return null;
            return result.record || result.data || result;
        } catch (error) {
            console.error(`Error creating ${collectionName}:`, error);
            throw error;
        }
    }

    /**
     * Create multiple records at once (creates one by one)
     */
    async bulkCreate(collectionName, dataArray) {
        try {
            const result = await this.callApi(
                "bulkinsert",
                collectionName,
                null,
                null,
                dataArray
            );
            if (result === null) return null;
            return result.data || result;
        } catch (error) {
            console.error(`Error bulk creating ${collectionName}:`, error);
            throw error;
        }
    }

    /**
     * Update multiple records at once via the backend bulkupdate operation.
     * Each entry must include `key` (or `id`) identifying the record, plus
     * the fields to update. Single round-trip.
     */
    async bulkUpdate(collectionName, recordsArray) {
        try {
            const result = await this.callApi(
                "bulkupdate",
                collectionName,
                null,
                null,
                recordsArray
            );
            if (result === null) return null;
            return result.data || result;
        } catch (error) {
            console.error(`Error bulk updating ${collectionName}:`, error);
            throw error;
        }
    }

    /**
     * Update a record
     */
    async update(collectionName, id, updates) {
        try {
            // Add the key/id to the updates data
            const dataWithKey = { ...updates, key: id };
            const result = await this.callApi(
                "update",
                collectionName,
                dataWithKey,
                { key: id }
            );
            if (result === null) return null;
            return result.record || result.data || result;
        } catch (error) {
            console.error(`Error updating ${collectionName}:`, error);
            throw error;
        }
    }

    /**
     * Delete a record
     */
    async delete(collectionName, id) {
        try {
            const result = await this.callApi("delete", collectionName, {
                key: id,
            });
            // If result is null (e.g. 204 No Content), consider it successful
            if (result === null) return true;
            return result.success !== false;
        } catch (error) {
            console.error(`Error deleting ${collectionName}:`, error);
            throw error;
        }
    }
}

// Create and export singleton instance
const dbAdapter = new DatabaseAdapter();
export { DatabaseAdapter };
export default dbAdapter;
